import { isBillingFinancialRoute } from '@/config/axios.config';
import type {
  BillingPlatformAccess,
  OrganizationBillingChargeResult,
  OrganizationBillingContext,
} from '@fluenceur/types';
import { isTaxResidenceCountry, PlanTier } from '@fluenceur/types';
import { serializePaywallUrl } from './paywall-url-state';
import type { InternalAxiosRequestConfig } from 'axios';
import { z } from 'zod';

interface BillingRequestScope {
  userId?: string;
  organizationId: string;
  brandId: string;
}

type ScopedRequest = { _billingScope?: BillingRequestScope | null };
let currentRequestScope: BillingRequestScope | null = null;

/** Request identity only; never a cache of billing rights or a persisted decision. */
export function setBillingRequestScope(scope: BillingRequestScope | null): void {
  currentRequestScope = scope ? { ...scope } : null;
}

export function getBillingRequestScope(): BillingRequestScope | null {
  return currentRequestScope ? { ...currentRequestScope } : null;
}

export function captureBillingRequestScope(config: InternalAxiosRequestConfig): void {
  (config as InternalAxiosRequestConfig & ScopedRequest)._billingScope = currentRequestScope;
}

export function isStaleBillingResponse(
  config: ScopedRequest | undefined,
  payload: unknown
): boolean {
  if (config && '_billingScope' in config) {
    if (
      config._billingScope?.userId !== currentRequestScope?.userId ||
      config._billingScope?.brandId !== currentRequestScope?.brandId ||
      config._billingScope?.organizationId !== currentRequestScope?.organizationId
    )
      return true;
  }
  if (!payload || typeof payload !== 'object') return false;
  const responseScope = payload as Partial<BillingRequestScope>;
  if (!responseScope.brandId || !responseScope.organizationId) return false;
  return (
    responseScope.brandId !== currentRequestScope?.brandId ||
    responseScope.organizationId !== currentRequestScope?.organizationId
  );
}

/** Only the server's bounded, internal routing decision may drive a billing redirect. */
export function readBillingAccess(value: unknown): BillingPlatformAccess | null {
  if (!value || typeof value !== 'object') return null;
  const access = value as BillingPlatformAccess;
  if (
    typeof access.allowed !== 'boolean' ||
    ![
      'subscription',
      'free_plan',
      'bypass',
      'card_required',
      'shopify_approval_required',
      'subscription_required',
      'integration_deleted',
      'integration_disconnected',
      'shopify_store_required',
    ].includes(access.reason) ||
    ![
      'none',
      'install_app',
      'reinstall_app',
      'reconnect_integration',
      'choose_plan',
      'add_card',
      'approve_shopify',
    ].includes(access.action) ||
    ![null, '/onboarding/shopify', '/dashboard', '/onboarding/billing', '/paywall'].includes(
      access.redirectTo
    )
  )
    return null;
  return access;
}

export const stripeInvoiceUrl = z
  .string()
  .url()
  .refine((value) => {
    try {
      const url = new URL(value);
      return (
        value === value.trim() &&
        url.protocol === 'https:' &&
        ['invoice.stripe.com', 'pay.stripe.com'].includes(url.hostname) &&
        !url.username &&
        !url.password &&
        !url.port
      );
    } catch {
      return false;
    }
  });

export function isShopifyApprovalUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      value === value.trim() &&
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      (url.hostname === 'admin.shopify.com' ||
        /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(url.hostname))
    );
  } catch {
    return false;
  }
}

const billingCardStateSchema = z
  .object({
    shopifyFreeApproval: z
      .object({
        requirement: z.enum(['not_required', 'required', 'pending', 'approved']),
        operationId: z.string().trim().min(1).nullable(),
        status: z
          .enum([
            'reserved',
            'processing',
            'pending',
            'approved',
            'declined',
            'expired',
            'uncertain',
          ])
          .nullable(),
        approvalUrl: z.string().url().refine(isShopifyApprovalUrl).nullable(),
        canApprove: z.boolean(),
        catalogCurrency: z
          .string()
          .regex(/^[A-Z]{3}$/)
          .nullable()
          .optional(),
      })
      .strict()
      .refine((value) => value.requirement !== 'pending' || !!value.operationId)
      .optional(),
    billingCurrency: z.enum(['EUR', 'USD', 'GBP', 'CAD']).nullable(),
    billingCountry: z.string().refine(isTaxResidenceCountry).nullable().optional(),
    checkoutAvailability: z.object({ freeCardSetup: z.boolean(), paid: z.boolean() }).strict(),
    paidCheckout: z
      .object({
        id: z.string().trim().min(1),
        planId: z.enum(['essential_monthly', 'growth_monthly']),
        currency: z.enum(['EUR', 'USD', 'GBP', 'CAD']),
        unitAmount: z.number().finite().int().positive().refine(Number.isSafeInteger),
        status: z.enum(['preparing', 'open', 'confirming', 'expired', 'needs_reconciliation']),
        expiresAt: z.string().datetime({ offset: true }).nullable(),
      })
      .strict()
      .nullable()
      .optional(),
    taxQualificationRequired: z.boolean().optional(),
    firstInvoice: z
      .object({
        id: z.string().uuid(),
        number: z.string().min(1).nullable(),
        currency: z.enum(['EUR', 'USD', 'GBP', 'CAD']),
        amountPaid: z.number().finite().int().positive().refine(Number.isSafeInteger),
        confirmedAt: z.string().datetime({ offset: true }),
        hostedInvoiceUrl: stripeInvoiceUrl.nullable(),
        invoicePdf: stripeInvoiceUrl.nullable(),
      })
      .strict()
      .nullable()
      .optional(),
    cardSetup: z
      .object({
        requirement: z.enum(['not_required', 'required', 'confirmed']),
        exemption: z.enum(['shopify', 'manual', 'impersonation']).nullable(),
        emailVerificationRequired: z.boolean(),
        identityRequired: z.boolean(),
        operation: z
          .object({
            id: z.string().trim().min(1),
            status: z.enum([
              'preparing',
              'open',
              'confirming',
              'expired',
              'blocked',
              'needs_reconciliation',
            ]),
            expiresAt: z.string().datetime({ offset: true }).nullable(),
          })
          .strict()
          .nullable(),
        retryAfterSeconds: z.number().finite().nonnegative().nullable(),
      })
      .strict(),
    capabilities: z
      .object({
        canReadBilling: z.boolean(),
        canCommitPaidPlan: z.boolean(),
        canReturnToFree: z.boolean(),
        canSetupCard: z.boolean(),
      })
      .strict(),
  })
  .strict();

/** Card state and capabilities are server facts, never permissive local defaults. */
export function readBillingContext(value: unknown): OrganizationBillingContext | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const context = value as OrganizationBillingContext;
  if (!readBillingAccess(context.access)) return null;
  if (
    context.access.reason === 'shopify_approval_required' &&
    (context.channel !== 'shopify' ||
      !context.shopifyFreeApproval ||
      !['required', 'pending'].includes(context.shopifyFreeApproval.requirement))
  )
    return null;
  if (
    context.checkoutAvailability?.paid === true &&
    (typeof context.taxQualificationRequired !== 'boolean' || context.paidCheckout === undefined)
  )
    return null;
  const cardState = billingCardStateSchema.safeParse({
    billingCurrency: context.billingCurrency,
    billingCountry: context.billingCountry,
    checkoutAvailability: context.checkoutAvailability,
    paidCheckout: context.paidCheckout,
    taxQualificationRequired: context.taxQualificationRequired,
    firstInvoice: context.firstInvoice,
    cardSetup: context.cardSetup,
    capabilities: context.capabilities,
    shopifyFreeApproval: context.shopifyFreeApproval,
  });
  return cardState.success ? context : null;
}

/** Only a canonical expiration of the same receipt ends its pending presentation. */
export function isPaidCheckoutPending(
  context: OrganizationBillingContext | null | undefined,
  receipt?: OrganizationBillingChargeResult | null
): boolean {
  const operation = context?.paidCheckout;
  if (operation && operation.status !== 'expired') return true;
  if (receipt?.kind !== 'pending' || receipt.planId === 'free') return false;
  return !(
    operation?.status === 'expired' &&
    context?.channel === 'stripe' &&
    receipt.channel === 'stripe' &&
    receipt.organizationId === context.organizationId &&
    receipt.brandId === context.brandId &&
    receipt.operationId === operation.id &&
    receipt.planId === operation.planId
  );
}

export function billingRedirectFor(access: BillingPlatformAccess, returnTo: string): string | null {
  if (access.allowed) return null;
  if (!access.redirectTo) {
    const current = new URL(returnTo, 'https://dashboard.example.test');
    // Closed organizations retain their financial status and documents under existing permissions.
    return access.reason === 'subscription_required' &&
      access.action === 'none' &&
      !isBillingFinancialRoute(current.pathname)
      ? '/settings/billing'
      : null;
  }
  if (access.redirectTo === '/dashboard' || access.redirectTo === '/onboarding/billing') {
    const current = new URL(returnTo, 'https://dashboard.example.test');
    if (current.pathname === '/dashboard' && current.searchParams.get('paywall') === 'plans')
      return null;
    return serializePaywallUrl('/dashboard', { paywall: 'plans' });
  }
  if (access.redirectTo !== '/paywall') return access.redirectTo;
  return serializePaywallUrl(`/dashboard?${new URLSearchParams({ redirect_url: returnTo })}`, {
    paywall: 'plans',
  });
}

/** Presentation of an unresolved billing step never grants business access. */
export function needsBillingModal(
  value: OrganizationBillingContext | null | undefined,
  scope: { brandId?: string | null; organizationId?: string | null }
): boolean {
  const context = readBillingContext(value);
  if (
    !context ||
    context.brandId !== scope.brandId ||
    context.organizationId !== scope.organizationId ||
    context.access.allowed ||
    !['/dashboard', '/onboarding/billing', '/paywall'].includes(context.access.redirectTo ?? '')
  )
    return false;
  if (context.access.reason === 'subscription_required')
    return context.access.action === 'choose_plan';
  if (context.access.reason === 'card_required')
    return context.access.action === 'add_card' && context.cardSetup.requirement === 'required';
  if (context.access.reason === 'shopify_store_required')
    return context.access.action === 'install_app' && context.channel === 'shopify';
  return (
    context.channel === 'shopify' &&
    context.effectiveTier === PlanTier.FREE &&
    context.access.reason === 'shopify_approval_required' &&
    context.access.action === 'approve_shopify' &&
    !!context.shopifyFreeApproval &&
    ['required', 'pending'].includes(context.shopifyFreeApproval.requirement)
  );
}
